






























































































































import { Component, Watch } from "vue-property-decorator";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import PickerDialogComponent from "../common/PickerDialog.vue";
import { 
  NumberOfMonths, 
  NumberOfWeeks, 
  NumberOfDays, 
  FinishedProjectsSettings,
  getDefaultNumberOfMonthsFromNumberOfWeeks,
} from "@/data/models/Settings";
import { createDefaultFinishedProjectsSettings } from "@/data/models/Client";
import { defineOwnFinishedProjectsSettingsClientAccess } from "@/permissions/ClientPermissions";
import { FinishedProjectsSettingsModule } from "@/store/modules/FinishedProjectsSettingsModule";
import { updateFinsihedProjectsSettings } from "@/data/repository/SettingsRepository";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class FinishedProjectsSettingsComponent extends SettingsPageMixin {
  isIntiallyLoading = true;

  numberOfMonthsOptions = Object.values(NumberOfMonths).filter(value => typeof value === 'number');
  numberOfWeeksOptions = Object.values(NumberOfWeeks).filter(value => typeof value === 'number');
  numberOfDaysOptions = Object.values(NumberOfDays).filter(value => typeof value === 'number');

  currentProjectsNotTouched = NumberOfMonths.NEVER;
  deleteProjectsNotProcessed = NumberOfWeeks.NEVER;
  moveFinishedProjects = NumberOfMonths.NEVER;
  moveProjectsClosed = NumberOfWeeks.NEVER;
  moveProjectsDamagePatternClosed = NumberOfWeeks.NEVER;
  moveProjectsOnHold = NumberOfMonths.THREE;
  recentlyFinishedProjects = NumberOfDays.TWO;

  initialFinishedProjectsSettings: FinishedProjectsSettings = createDefaultFinishedProjectsSettings();

  @Watch("finishedProjectsSettings")
  onFinishedProjectSettingsChanged() {
    const finishedProjectsSettings = this.finishedProjectsSettings;
    finishedProjectsSettings.moveProjectsOnHold = getDefaultNumberOfMonthsFromNumberOfWeeks(finishedProjectsSettings.moveProjectsOnHold);

    if (
      finishedProjectsSettings && 
      !finishedProjectsSettings.areDefaultFinishedProjectsSettings
    ) {
      this.initializeParameters(finishedProjectsSettings);  
    }
  }

  get hasChanged() {
    return (
      this.currentProjectsNotTouched !== this.initialFinishedProjectsSettings.currentProjectsNotTouched ||
      this.deleteProjectsNotProcessed !== this.initialFinishedProjectsSettings.deleteProjectsNotProcessed ||
      this.moveFinishedProjects !== this.initialFinishedProjectsSettings.moveFinishedProjects ||
      this.moveProjectsClosed !== this.initialFinishedProjectsSettings.moveProjectsClosed ||
      this.moveProjectsDamagePatternClosed !== this.initialFinishedProjectsSettings.moveProjectsDamagePatternClosed ||
      this.moveProjectsOnHold !== this.initialFinishedProjectsSettings.moveProjectsOnHold ||
      this.recentlyFinishedProjects !== this.initialFinishedProjectsSettings.recentlyFinishedProjects
    );
  }

  get readonly() {
    return !this.isEditMode || !this.canEdit;
  }

  get formatNumberOfMonths() {
    const qualityMapping = {
      [NumberOfMonths.NEVER]: "never",
      [NumberOfMonths.THREE]: "three",
      [NumberOfMonths.SIX]: "six",
      [NumberOfMonths.NINE]: "nine",
      [NumberOfMonths.TWELVE]: "twelve",
      [NumberOfMonths.TWENTY_FOUR]: "twentyFour",
    };

    return (value: NumberOfMonths) => {
      const qualityString = qualityMapping[value] || "never"; // Fallback to "never" if value is not in the mapping
      return this.$t(`settings.system.finishedProjects.numberOfMonths.${qualityString}`).toString();
    };
  }

  get formatNumberOfWeeks() {
    const qualityMapping = {
      [NumberOfWeeks.NEVER]: "never",
      [NumberOfWeeks.ONE]: "one",
      [NumberOfWeeks.TWO]: "two",
      [NumberOfWeeks.FOUR]: "four",
      [NumberOfWeeks.EIGHT]: "eight",
      [NumberOfWeeks.TWELVE]: "twelve",
    };

    return (value: NumberOfWeeks) => {
      const qualityString = qualityMapping[value] || "never"; // Fallback to "never" if value is not in the mapping
      return this.$t(`settings.system.finishedProjects.numberOfWeeks.${qualityString}`).toString();
    };
  }

  get formatNumberOfDays() {
    const qualityMapping = {
      [NumberOfDays.NEVER]: "never",
      [NumberOfDays.ONE]: "one",
      [NumberOfDays.TWO]: "two",
      [NumberOfDays.THREE]: "three",
      [NumberOfDays.FOUR]: "four",
    };

    return (value: NumberOfDays) => {
      const qualityString = qualityMapping[value] || "never"; // Fallback to "never" if value is not in the mapping
      return this.$t(`settings.system.finishedProjects.numberOfDays.${qualityString}`).toString();
    };
  }

  get finishedProjectsSettings() {
    return FinishedProjectsSettingsModule.finishedProjectsSettings;
  }

  get canEdit() {
    return defineOwnFinishedProjectsSettingsClientAccess();
  }

  initializeParameters(
    finishedProjectsSettings: FinishedProjectsSettings,
  ) {

    this.currentProjectsNotTouched = finishedProjectsSettings.currentProjectsNotTouched;
    this.deleteProjectsNotProcessed = finishedProjectsSettings.deleteProjectsNotProcessed;
    this.moveFinishedProjects = finishedProjectsSettings.moveFinishedProjects;
    this.moveProjectsClosed = finishedProjectsSettings.moveProjectsClosed;
    this.moveProjectsDamagePatternClosed = finishedProjectsSettings.moveProjectsDamagePatternClosed;
    this.moveProjectsOnHold = finishedProjectsSettings.moveProjectsOnHold;
    this.recentlyFinishedProjects = finishedProjectsSettings.recentlyFinishedProjects;

    this.initialFinishedProjectsSettings = finishedProjectsSettings;

    this.isEditMode = false;
  }

  async created() {
    this.isIntiallyLoading = true;
    const finishedProjectsSettings = this.finishedProjectsSettings ?? createDefaultFinishedProjectsSettings();

    this.initializeParameters(finishedProjectsSettings);
    
    this.isIntiallyLoading = false;
  }

  async doSave() {
    const finishedProjectsSettingsUpdate: FinishedProjectsSettings = {
      currentProjectsNotTouched: this.currentProjectsNotTouched,
      deleteProjectsNotProcessed: this.deleteProjectsNotProcessed,
      moveFinishedProjects: this.moveFinishedProjects,
      moveProjectsClosed: this.moveProjectsClosed,
      moveProjectsDamagePatternClosed: this.moveProjectsDamagePatternClosed,
      moveProjectsOnHold: this.moveProjectsOnHold,
      recentlyFinishedProjects: this.recentlyFinishedProjects,
      
      areDefaultFinishedProjectsSettings: false,
    }
    
    await updateFinsihedProjectsSettings(finishedProjectsSettingsUpdate);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.initialFinishedProjectsSettings = finishedProjectsSettingsUpdate;
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.system.finishedProjects.title").toString());
  }
}
